import { useId, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Balance } from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'
import { uploadFile } from '@/api/upload'
import { useAccess } from '@/access/useAccess'
import { formatDecimalBR, MONTHS, monthName, parseBRL } from '@/lib/format'
import { Button, Dropzone, Field, Modal, useToast } from '@/ui'
import { createBalance, replaceBalance } from './api'
import styles from './BalanceModal.module.css'

interface Props {
  /** When set, the modal replaces this balance (PATCH) instead of publishing a new one. */
  balance?: Balance
  onClose: () => void
}

/** Month before today: the balance usually closes after the month ends. */
function defaultReference(now = new Date()) {
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { month: prev.getMonth() + 1, year: prev.getFullYear() }
}

export function BalanceModal({ balance, onClose }: Props) {
  const { condoId } = useAccess()
  const toast = useToast()
  const queryClient = useQueryClient()
  const monthId = useId()
  const replacing = !!balance
  const def = defaultReference()

  const [month, setMonth] = useState(balance?.reference_month ?? def.month)
  const [year, setYear] = useState(String(balance?.reference_year ?? def.year))
  const [income, setIncome] = useState(balance ? formatDecimalBR(balance.total_income) : '')
  const [expenses, setExpenses] = useState(balance ? formatDecimalBR(balance.total_expenses) : '')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const incomeValue = parseBRL(income)
  const expensesValue = parseBRL(expenses)
  const yearValue = /^\d{4}$/.test(year.trim()) ? Number(year) : null
  const incomeError =
    income.trim() !== '' && incomeValue === null ? 'Use o formato 184.320,00.' : undefined
  const expensesError =
    expenses.trim() !== '' && expensesValue === null ? 'Use o formato 171.450,35.' : undefined
  const valid = incomeValue !== null && expensesValue !== null && yearValue !== null && !!file

  async function publish() {
    if (!file || incomeValue === null || expensesValue === null || yearValue === null) return
    setSubmitting(true)
    try {
      const upload = await uploadFile({
        file,
        resourceType: 'balance_report',
        condominiumId: condoId,
      })
      const body = {
        reference_month: month,
        reference_year: yearValue,
        total_income: incomeValue,
        total_expenses: expensesValue,
        report_upload_id: upload.upload_id,
      }
      if (balance) await replaceBalance(condoId, balance.id, body)
      else await createBalance(condoId, body)
      await queryClient.invalidateQueries({ queryKey: queryKeys.balances(condoId) })
      toast(`Balancete de ${monthName(month)} ${replacing ? 'atualizado' : 'publicado'}`, {
        icon: 'check',
      })
      onClose()
    } catch {
      toast('Não foi possível enviar o balancete. Tente novamente.', { icon: 'error' })
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={replacing ? 'Substituir balancete' : 'Enviar balancete mensal'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button disabled={!valid} loading={submitting} onClick={publish}>
            {replacing ? 'Salvar balancete' : 'Publicar balancete'}
          </Button>
        </>
      }
    >
      <div className={styles.pair}>
        <div className={styles.select}>
          <label htmlFor={monthId} className={styles.label}>
            Mês de referência
          </label>
          <select
            id={monthId}
            className={styles.control}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <Field label="Ano" value={year} onChange={setYear} placeholder="2026" />
      </div>
      <div className={styles.pair}>
        <Field
          label="Receita total"
          value={income}
          onChange={setIncome}
          placeholder="184.320,00"
          error={incomeError}
        />
        <Field
          label="Despesa total"
          value={expenses}
          onChange={setExpenses}
          placeholder="171.450,35"
          error={expensesError}
        />
      </div>
      <Dropzone
        file={file}
        onFile={setFile}
        accept="application/pdf"
        title="Anexar o relatório completo (PDF)"
        description="O detalhamento das despesas fica no relatório, não no app"
      />
    </Modal>
  )
}
